import type { TileMap } from '../turn/types';

/**
 * All road texture keys loaded in preload().
 *
 * Naming follows screen (isometric) compass directions:
 *   NE edge = grid N (row-1)
 *   SE edge = grid E (col+1)
 *   SW edge = grid S (row+1)
 *   NW edge = grid W (col-1)
 */
export const ROAD_TEXTURES = {
  STRAIGHT_NESW: 'road-straight-nesw',
  STRAIGHT_NWSE: 'road-straight-nwse',
  BEND_N: 'road-bend-n',
  BEND_E: 'road-bend-e',
  BEND_S: 'road-bend-s',
  BEND_W: 'road-bend-w',
  TCROSS_NE: 'road-tcross-ne',
  TCROSS_NW: 'road-tcross-nw',
  TCROSS_SE: 'road-tcross-se',
  TCROSS_SW: 'road-tcross-sw',
  XCROSS: 'road-xcross',
} as const;

export type RoadTextureKey = typeof ROAD_TEXTURES[keyof typeof ROAD_TEXTURES];

/** All texture keys — use in preload() to register every sprite. */
export const ALL_ROAD_TEXTURE_KEYS: RoadTextureKey[] = Object.values(ROAD_TEXTURES);

/*
 * 4-bit neighbor bitmask: N=1, E=2, S=4, W=8
 *
 * Grid → screen edge mapping:
 *   grid N (row-1) → screen NE edge
 *   grid E (col+1) → screen SE edge
 *   grid S (row+1) → screen SW edge
 *   grid W (col-1) → screen NW edge
 *
 * Bitmask → sprite:
 *   0  (----) isolated      → STRAIGHT_NESW (default)
 *   1  (N---) dead-end N    → STRAIGHT_NESW
 *   2  (-E--) dead-end E    → STRAIGHT_NWSE
 *   3  (NE--) bend          → BEND_W   (NE↔SE edges, opening faces W)
 *   4  (--S-) dead-end S    → STRAIGHT_NESW
 *   5  (N-S-) straight N/S  → STRAIGHT_NESW
 *   6  (-ES-) bend          → BEND_N   (SE↔SW edges, opening faces N)
 *   7  (NES-) T-junction    → TCROSS_SE (missing NW)
 *   8  (---W) dead-end W    → STRAIGHT_NWSE
 *   9  (N--W) bend          → BEND_S   (NE↔NW edges, opening faces S)
 *  10  (-E-W) straight E/W  → STRAIGHT_NWSE
 *  11  (NE-W) T-junction    → TCROSS_NE (missing SW)
 *  12  (--SW) bend          → BEND_E   (SW↔NW edges, opening faces E)
 *  13  (N-SW) T-junction    → TCROSS_NW (missing SE)
 *  14  (-ESW) T-junction    → TCROSS_SW (missing NE)
 *  15  (NESW) crossroad     → XCROSS
 */
const BITMASK_TO_TEXTURE: RoadTextureKey[] = [
  /* 0  */ ROAD_TEXTURES.STRAIGHT_NESW,
  /* 1  */ ROAD_TEXTURES.STRAIGHT_NESW,
  /* 2  */ ROAD_TEXTURES.STRAIGHT_NWSE,
  /* 3  */ ROAD_TEXTURES.BEND_W,
  /* 4  */ ROAD_TEXTURES.STRAIGHT_NESW,
  /* 5  */ ROAD_TEXTURES.STRAIGHT_NESW,
  /* 6  */ ROAD_TEXTURES.BEND_N,
  /* 7  */ ROAD_TEXTURES.TCROSS_SE,
  /* 8  */ ROAD_TEXTURES.STRAIGHT_NWSE,
  /* 9  */ ROAD_TEXTURES.BEND_S,
  /* 10 */ ROAD_TEXTURES.STRAIGHT_NWSE,
  /* 11 */ ROAD_TEXTURES.TCROSS_NE,
  /* 12 */ ROAD_TEXTURES.BEND_E,
  /* 13 */ ROAD_TEXTURES.TCROSS_NW,
  /* 14 */ ROAD_TEXTURES.TCROSS_SW,
  /* 15 */ ROAD_TEXTURES.XCROSS,
];

/** Returns true if the terrain type participates in road auto-tiling. */
function isRoadLike(type: string): boolean {
  return type === 'road' || type === 'bridge';
}

/** Compute the 4-bit neighbor bitmask for a road tile. N=1, E=2, S=4, W=8. */
export function roadNeighborMask(
  map: TileMap,
  col: number,
  row: number,
): number {
  let mask = 0;
  if (row > 0 && isRoadLike(map.tiles[row - 1][col].type)) mask |= 1;               // N
  if (col < map.width - 1 && isRoadLike(map.tiles[row][col + 1].type)) mask |= 2;    // E
  if (row < map.height - 1 && isRoadLike(map.tiles[row + 1][col].type)) mask |= 4;   // S
  if (col > 0 && isRoadLike(map.tiles[row][col - 1].type)) mask |= 8;                // W
  return mask;
}

/**
 * Return the single road sprite texture key for a road tile based on its
 * orthogonal neighbors (4-bit auto-tiling).
 */
export function getRoadTextureKey(
  map: TileMap,
  col: number,
  row: number,
): RoadTextureKey {
  return BITMASK_TO_TEXTURE[roadNeighborMask(map, col, row)];
}
